import React, { Component } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import classNames from 'classnames';
import { chunk } from 'lodash';
import { Map, List, Set } from 'immutable';

import { ButtonSmall } from '../../Button';
import * as dateHelper from '../../../lib/dates';
import * as styles from './styles.css';
import RepeatOverlay from './RepeatOverlay';
import DateCommandInput from './DateCommandInput';
import WeekCell from './WeekCell';
import CalendarDate, { CalendarDateTest } from './CalendarDate';
import LoadingIndicator from '../../LoadingIndicator/LoadingIndicator';

/**
 * Calendar, built internally for LeanLink, with the main feature to handle dates as a set of dates.
 * Used mainly as input, but also for visualization
 *
 * ### props
 * holidays => displays holidays given from Backend
 * periodHighlights => add a highlight of date equally divided on the date, see example below
 *  new Map({ 2020-09-22: [{ name: 'kaffe', background: '#f00'}, {...}]})
 * hasSplittedDay => bool connected to the above feature, if the period is divided on two dates
 * or a single date
 * highlighted => older version of above, will highlight a full date
 *
 * Note: there are many ESLint errors given from this component, but it is working though a
 * refactor of it would most likely improve the readability by a lot!
 *
 * Missing feature: Select interval by fromDate & toDate instead of week-number
 */

export function defaultDisplayMonth(props) {
  if (props.month) {
    return props.month;
  }

  if (props.input.value && props.input.value.size > 0) {
    return props.input.value.first();
  }

  if (props.highlighted && props.highlighted.size > 0 && !props.isHighlightedInfo) {
    return props.highlighted.first();
  }

  return dateHelper.getDate(new Date());
}

export default class Calendar extends Component {
  static propTypes = {
    holidays: PropTypes.instanceOf(Map).isRequired,
    periodHighlights: PropTypes.instanceOf(Map),
    highlighted: PropTypes.instanceOf(List),
    input: PropTypes.shape({
      value: PropTypes.oneOfType([
        PropTypes.instanceOf(List),
        PropTypes.string,
      ]).isRequired,
      onBlur: PropTypes.func.isRequired,
      onChange: PropTypes.func.isRequired,
    }).isRequired,
    onChangeMonth: PropTypes.func,
    month: PropTypes.string,
    isLoading: PropTypes.bool,
    selectWeekDays: PropTypes.bool,
    isHighlightedInfo: PropTypes.bool,
    daysWeekTitle: PropTypes.string,
    variant: PropTypes.string,
    hideRepeat: PropTypes.bool,
    hideClear: PropTypes.bool,
    hideInput: PropTypes.bool,
    hideToggleSelectWeekDays: PropTypes.bool,
    noBottomMargin: PropTypes.bool,
    activePast: PropTypes.bool,
    clearPanel: PropTypes.bool,
    singleDay: PropTypes.bool,
    placement: PropTypes.oneOf(['left', 'right']),
    unitTest: PropTypes.bool,
    public: PropTypes.bool,
    hasSplittedDay: PropTypes.bool,
  };

  static defaultProps = {
    holidays: new Map(),
    highlighted: undefined,
    onChangeMonth: undefined,
    month: undefined,
    isLoading: false,
    selectWeekDays: true,
    isHighlightedInfo: false,
    daysWeekTitle: 'dagarsvecka',
    variant: 'primary',
    hideRepeat: false,
    hideClear: false,
    hideInput: false,
    hideToggleSelectWeekDays: false,
    noBottomMargin: false,
    activePast: false,
    clearPanel: false,
    singleDay: false,
    unitTest: false,
    placement: 'left',
    periodHighlights: new Map(),
    public: false,
    hasSplittedDay: false,
  };

  constructor(props) {
    super(props);
    this.state = {
      // Stores the date part of the date, specified by dateHelper.getDate
      displayMonth: defaultDisplayMonth(props),
      showRepeatOverlay: false,
      selectWeekDays: props.selectWeekDays,
    };

    this.ref = null;
  }

  componentWillMount() {
    if (typeof this.props.input.value === 'string') {
      this.props.input.onChange(new List());
    }
    if (this.props.selectWeekDays === false) {
      this.setState({ selectWeekDays: this.props.selectWeekDays });
    }
  }

  componentWillReceiveProps(nextProps) {
    if (this.props.input.value.size === 0 && nextProps.input.value.size > 1 && !nextProps.month) {
      this.setState({ displayMonth: nextProps.input.value.first() });
    }

    if (this.props.highlighted && nextProps.highlighted
      && this.props.highlighted.first() !== nextProps.highlighted.first()
      && !nextProps.month && !this.props.isHighlightedInfo) {
      this.setState({ displayMonth: nextProps.highlighted.first() });
    }

    if (this.props.month !== nextProps.month && nextProps.month) {
      this.setState({ displayMonth: nextProps.month });
    }

    if (this.props.selectWeekDays !== nextProps.selectWeekDays) {
      this.setState({ selectWeekDays: nextProps.selectWeekDays });
    }
  }

  value = (props = this.props) => props.input.value || new List();

  changeMonth(modifier) {
    const displayMonth = dateHelper.getDate(moment(this.state.displayMonth).add(modifier, 'month'));
    if (this.props.onChangeMonth) {
      this.props.onChangeMonth(displayMonth);
    }

    if (!this.props.month) {
      this.setState({ displayMonth });
    }
  }

  nextMonth() {
    this.changeMonth(1);
  }

  previousMonth() {
    this.changeMonth(-1);
  }

  toggleSelectDate = (date) => {
    if (this.value().includes(date)) {
      this.deselectDates([date]);
    } else {
      this.selectDates([date]);
    }
  };

  toggleDates = (dates) => {
    // If any of the dates are selected, deselect all dates, otherwise, add all dates
    const someDateInWeekIsSelected = this.value().some(d => dates.includes(d));

    if (someDateInWeekIsSelected) {
      return this.deselectDates(dates);
    }

    if (!this.props.highlighted) {
      return this.selectDates(dates);
    }

    const highlightedIntersection = this.props.highlighted.toSet().intersect(new Set(dates));
    return this.selectDates(
      highlightedIntersection.size > 0 ? highlightedIntersection.toArray() : dates,
    );
  }

  toggleSelectWeek = (weekDate) => {
    if (this.props.singleDay) {
      return null;
    }
    const weekDates = this.state.selectWeekDays ?
      dateHelper.selectWeekdays(null, dateHelper.getDatesInWeekWithDate(weekDate)) :
      dateHelper.getDatesInWeekWithDate(weekDate);
    return this.toggleDates(weekDates);
  };

  toggleSelectMonth = () => {
    if (this.props.singleDay) {
      return null;
    }
    const monthDates = this.state.selectWeekDays ?
      dateHelper.selectWeekdays(null, dateHelper.getDatesInMonthWithDate(this.state.displayMonth)) :
      dateHelper.getDatesInMonthWithDate(this.state.displayMonth);
    return this.toggleDates(monthDates);
  }

  selectDates(dates) {
    if (this.props.singleDay) {
      this.updateDates(dates);
      return;
    }
    this.updateDates(this.value().push(...dates));
  }

  deselectDates(dates) {
    this.updateDates(this.value().filter(d => !dates.includes(d)));
  }

  addHighlighted = () => {
    this.updateDates(this.value().concat(this.props.highlighted));
  };

  /**
   *
   * @param dates Immutable List
   */
  updateDates = dates =>
    this.props.input.onBlur((new Set(dates)).toList()
      .sort((date1, date2) => (new Date(date1) - new Date(date2))));

  clearDates = () => this.updateDates(new List());

  render() {
    const CalendarDateComponent = this.props.unitTest ? CalendarDateTest : CalendarDate;
    const dates = dateHelper.getDatesForDisplayMonth(this.state.displayMonth);
    const displayWeeks = chunk(dates, 7).map((weekDates) => {
      const weekHasDatesThatAreSelected = dateHelper.dateInWeekIsSelected(
        this.value().toJS(),
        weekDates[0],
      );
      const weekNumber = moment(new Date(weekDates[0])).isoWeek();
      return (
        <tr key={weekNumber}>
          <WeekCell
            weekNumber={weekNumber}
            selected={weekHasDatesThatAreSelected}
            onClick={() => this.toggleSelectWeek(weekDates[0])}
          />

          {weekDates.map(date => (
            <CalendarDateComponent
              unitTest={this.props.unitTest}
              key={date}
              date={date}
              highlighted={
                this.props.highlighted ? this.props.highlighted.includes(date) : undefined
              }
              holiday={this.props.holidays.get(date)}
              selected={this.value().includes(date)}
              onClick={this.toggleSelectDate}
              variant={this.props.variant}
              activePast={this.props.activePast}
              allPeriodHighlights={this.props.periodHighlights}
              hasSplittedDay={this.props.hasSplittedDay}
              withPeriods={this.props.periodHighlights && this.props.periodHighlights.size > 0}
              isPublic={this.props.public}
            />
          ))}
        </tr>
      );
    });

    // Loop through each week between first week and last week, and print out monday through sunday
    return (

      <div
        className={classNames('panel panel-default', {
          [styles.noBottomMargin]: this.props.noBottomMargin,
          [styles.clearPanel]: this.props.clearPanel,
        })}
        data-testid="calendar"
      >
        <header
          className="panel-heading clearfix"
          ref={(ref) => {
            this.ref = ref;
          }}
        >
          <span data-testid="displayMonth">
            <span
              onClick={this.toggleSelectMonth}
              role="button"
              tabIndex="0"
              data-testid="toggleSelectMonth"
            >
              {moment(this.state.displayMonth).format('MMMM')}
            </span>
            &nbsp;
            {moment(this.state.displayMonth).format('YYYY')}
          </span>

          <div className={`pull-right ${styles.headerButtonContainer}`}>
            <LoadingIndicator isLoading={this.props.isLoading} /> &nbsp;

            {this.props.highlighted && !this.props.isHighlightedInfo && (
              <ButtonSmall
                icon="calendar-plus-o text-primary"
                onClick={() => this.addHighlighted()}
                testID="allHighlightedButton"
              >
                Markera alla datum
              </ButtonSmall>
            )}

            {!this.props.hideToggleSelectWeekDays && (
              <ButtonSmall
                onClick={() => this.setState({ selectWeekDays: !this.state.selectWeekDays })}
                data-testid="toggleSelectWeekDays"
              >
                {this.state.selectWeekDays ? `5-${this.props.daysWeekTitle}` : `7-${this.props.daysWeekTitle}`}
              </ButtonSmall>
            )}

            <ButtonSmall
              icon="chevron-left text-primary"
              onClick={() => this.previousMonth()}
              testID="prevMonthButton"
            />

            <ButtonSmall
              icon="chevron-right text-primary"
              onClick={() => this.nextMonth()}
              testID="nextMonthButton"
            />

            {!this.props.hideRepeat && (
              <React.Fragment>
                <ButtonSmall
                  icon="repeat"
                  data-testid="calendarRepeat"
                  onClick={() => this.setState({ showRepeatOverlay: true })}
                />

                <RepeatOverlay
                  target={this.ref}
                  show={this.state.showRepeatOverlay}
                  onHide={() => this.setState({ showRepeatOverlay: false })}
                  onComplete={this.updateDates}
                  dates={this.value().toArray()}
                  placement={this.props.placement === 'left' ? 'right' : 'left'}
                />
              </React.Fragment>
            )}
            {!this.props.hideClear && (
              <ButtonSmall testID="clearDatesButton" icon="trash" onClick={this.clearDates} />
            )}
          </div>

        </header>

        <div className="panel-body no-padding">
          <table className={classNames(['table table-condensed', styles.calendarTable])}>
            <thead>
              <tr>
                <th className={`${styles.weekdayCell} ${styles.weekHeader}`}>V</th>
                <th className={styles.weekdayCell}>Må</th>
                <th className={styles.weekdayCell}>Ti</th>
                <th className={styles.weekdayCell}>On</th>
                <th className={styles.weekdayCell}>To</th>
                <th className={styles.weekdayCell}>Fr</th>
                <th className={styles.weekdayCell}>Lö</th>
                <th className={styles.weekdayCell}>Sö</th>
              </tr>
            </thead>
            <tbody>
              {displayWeeks}
            </tbody>
          </table>
        </div>

        {!this.props.hideInput && (
          <footer className="panel-footer no-padding">
            <DateCommandInput onChange={this.updateDates} />
          </footer>
        )}
      </div>
    );
  }
}
